import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { formatZAR } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ArrowRight } from "lucide-react";
import { ModuleHeader, StatCard, StatusPill, EmptyState } from "./AdminUI";
import { ALL_ORDER_STATUSES, PAYMENT_LABELS, statusLabel } from "./adminShared";

const QUICK = [
    { to: "/admin/orders", label: "Open orders inbox" },
    { to: "/admin/menu", label: "Mark items sold out" },
    { to: "/admin/specials", label: "Update today's specials" },
    { to: "/admin/settings", label: "Change hours & contact details" },
];

function AcceptingToggle({ value, onChange }) {
    return (
        <div className="flex items-center justify-between gap-4 p-5 border border-brand-line rounded-sm bg-brand-charcoal">
            <div>
                <p className="text-sm text-brand-cream">Online ordering</p>
                <p className="text-xs text-brand-mute mt-1">{value ? "Customers can place orders now." : "Checkout is paused for customers."}</p>
            </div>
            <Switch data-testid="dashboard-accepting-toggle" checked={value} onCheckedChange={onChange} />
        </div>
    );
}

function RecentOrders({ orders }) {
    if (!orders.length) return <EmptyState>No orders yet.</EmptyState>;
    return (
        <div className="border border-brand-line rounded-sm divide-y divide-brand-line bg-brand-charcoal">
            {orders.map((o) => (
                <div key={o.id} data-testid={`dashboard-recent-${o.order_number}`} className="flex flex-wrap items-center gap-4 px-5 py-3 text-sm">
                    <span className="font-display text-brand-gold w-20">{o.order_number}</span>
                    <span className="flex-1 min-w-[140px] truncate">{o.customer?.name}</span>
                    <span className="text-xs text-brand-mute hidden sm:inline">{PAYMENT_LABELS[o.payment_method] || o.payment_method}</span>
                    <StatusPill status={o.status} label={statusLabel(ALL_ORDER_STATUSES, o.status)} />
                    <span className="w-24 text-right">{formatZAR(o.total)}</span>
                </div>
            ))}
        </div>
    );
}

export default function DashboardModule() {
    const [d, setD] = useState(null);
    const load = () => client.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const toggleAccepting = async (v) => {
        await client.patch("/admin/settings", { accepting_orders: v });
        toast.success(v ? "Online ordering is on" : "Online ordering paused");
        load();
    };

    if (!d) return <p className="text-brand-mute">Loading dashboard…</p>;

    return (
        <div data-testid="admin-dashboard">
            <ModuleHeader eyebrow="Overview" title="Dashboard" subtitle="Today at a glance. Figures use South African time." />
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
                <StatCard testId="stat-orders-today" label="Orders today" value={d.orders_today} />
                <StatCard testId="stat-revenue-today" label="Revenue today" value={formatZAR(d.revenue_today)} hint="Excludes cancelled" />
                <StatCard testId="stat-open-orders" label="Open orders" value={d.open_orders} hint={`${d.new_orders} new`} accent={d.new_orders > 0} />
                <StatCard testId="stat-new-enquiries" label="New enquiries" value={d.new_enquiries} accent={d.new_enquiries > 0} />
                <StatCard testId="stat-sold-out" label="Sold out today" value={d.sold_out_today} hint={`${d.hidden_items} hidden items`} />
                <StatCard testId="stat-active-specials" label="Active specials" value={d.active_specials} hint={`${d.menu_items} menu items`} />
            </div>
            <div className="grid lg:grid-cols-[1fr_320px] gap-6 mt-10">
                <section>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-xl">Latest orders</h2>
                        <Link to="/admin/orders" className="text-xs text-brand-gold hover:underline" data-testid="dashboard-view-orders">View all</Link>
                    </div>
                    <RecentOrders orders={d.recent_orders} />
                </section>
                <aside className="space-y-4">
                    <AcceptingToggle value={d.accepting_orders} onChange={toggleAccepting} />
                    <div className="border border-brand-line rounded-sm bg-brand-charcoal divide-y divide-brand-line">
                        {QUICK.map((q) => (
                            <Link key={q.to + q.label} to={q.to} className="flex items-center justify-between px-5 py-3 text-sm text-brand-cream/80 hover:text-brand-gold transition-colors">
                                {q.label} <ArrowRight size={14} />
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
}
